import { useCallback, useContext, useEffect } from "react";

import { capitalize, Stack } from "@mui/material";
import {
  StripeRecurringInterval,
  getDollarValue,
  formatCurrencyAmount,
  getCurrencyFromCountry,
  formatSmartNarrowCurrencyFromCents,
  OrganizationCountry,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useController, useWatch } from "react-hook-form";

import { getStripeSubscriptionRecurrence } from "../../../../components/PaymentProcessor/helper";
import { FrontendFormContext } from "../../../../contexts/FrontendFormContext";
import { useLocaleContext } from "../../../../contexts/LocaleContext";
import { FormType } from "../../../../gql/gql-types";
import { useAmplitude } from "../../../../hooks/amplitude/useAmplitude";
import { DonationPaymentInput, useFormV2Context } from "../../context/FormV2Context";
import { HeartsAnimationContext } from "../../context/heartsAnimationContext";

import { AmplitudeEvents } from "@/constants/amplitude";
import { useShapes } from "@/features/FormV2/sharedComponents/BackgroundShapes/ShapesContext";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
import { FormRadio, FormRadioGroup } from "@/forms-design-system/FormRadio";
import { FormTab, FormTabGroup } from "@/forms-design-system/FormTab";
import { FormTextField } from "@/forms-design-system/FormTextField/FormTextField";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { HeartsFormTab } from "@/forms-design-system/HeartsFormTab";
import { isMax, isMin, isRequired, mergeValidators } from "@/helpers/validators";
import { useTranslate } from "@/hooks/useTranslate";

export const DONATION_AMOUNT_MAX = 100000000;
export const MIN_DONATION_AMOUNT = 99;

export const Amounts = () => {
  const { t } = useTranslate();
  const searchParams = useSearchParams();
  const { isoLocale } = useLocaleContext();
  const { setDisplayedFormAmount, setStripeRecurrenceInterval } = useContext(FrontendFormContext);
  const { isPreview } = usePreviewContext();
  const { rotate, reverseRotate } = useShapes();
  const { triggerHeartsAnimation } = useContext(HeartsAnimationContext);
  const isGhostPreview = useIsGhostPreview("Amounts");
  const amountParam = searchParams.get("amount");
  const defaultAmountQueryParam = amountParam && !isNaN(Number(amountParam)) ? Number(amountParam) : undefined;
  const { logAmplitudeEvent } = useAmplitude();

  const {
    formObject: donationForm,
    form,
    suggestedAmounts,
    hasSuggestedAmountsPerType,
    amountRecurrenceInterval,
    defaultDonationAmountVariant,
    setHasChangedDonationAmount,
    hasChangedDonationAmount,
  } = useFormV2Context(FormType.DonationForm);
  const currency = getCurrencyFromCountry(donationForm?.organization?.country);

  const selectedAmount = useWatch({ control: form.control, name: "amount" });
  const selectedAmountId = useWatch({ control: form.control, name: "selectedAmountId" });
  const recurrenceInterval = useWatch({ control: form.control, name: "recurrenceInterval" });

  const hideSuggestedAmounts = suggestedAmounts.length === 0;
  const suggestedAmountsToShow = suggestedAmounts.filter(
    (amount) => getStripeSubscriptionRecurrence(amount.recurrenceInterval) === recurrenceInterval
  );

  const selectedSuggestedAmountObject = suggestedAmounts.find((amount) => amount.id === selectedAmountId);

  const handleRecurringClick = (recurrenceInterval: StripeRecurringInterval | null) => {
    form.setValue("recurrenceInterval", recurrenceInterval);
    const newAmountAndId = suggestedAmounts
      .filter((amount) => getStripeSubscriptionRecurrence(amount.recurrenceInterval) === recurrenceInterval)
      .find((amount) => amount.amount === selectedAmount);
    form.setValue("selectedAmountId", newAmountAndId?.id);
    setStripeRecurrenceInterval(recurrenceInterval);
  };

  // Add validations for the amount field
  useController<DonationPaymentInput>({
    control: form.control,
    name: "amount",
    rules: mergeValidators(
      isRequired(t("donationForm", "required")),
      isMin(MIN_DONATION_AMOUNT, t("donationForm", "amountLessThan")),
      isMax(
        DONATION_AMOUNT_MAX,
        t("donationForm", "amountHigherThan", {
          amount: formatCurrencyAmount({
            value: getDollarValue(DONATION_AMOUNT_MAX),
            locale: isoLocale,
            currency,
          }),
        })
      )
    ),
  });

  const handleOnChangeAmountFromSuggestion = useCallback(
    ({ value, triggeredByUseEffect = false }: { value: string; triggeredByUseEffect: boolean }) => {
      const floatValue = parseFloat(value);
      const amount = isNaN(floatValue) ? 0 : floatValue;

      setDisplayedFormAmount(amount);
      triggerHeartsAnimation();
      if (amount > selectedAmount) {
        rotate();
      } else if (amount < selectedAmount) {
        reverseRotate();
      }

      form.setValue("amount", amount, { shouldValidate: true });
      form.setValue("manualAmount", (amount / 100).toString(), { shouldValidate: true });

      const id = suggestedAmountsToShow.find((suggestedAmount) => suggestedAmount.amount === amount)!.id;
      form.setValue("selectedAmountId", id);

      if (!triggeredByUseEffect && defaultDonationAmountVariant === "b" && !hasChangedDonationAmount) {
        setHasChangedDonationAmount(true);
        logAmplitudeEvent(AmplitudeEvents.DefaultAmountChanged);
      }
    },
    [
      setDisplayedFormAmount,
      triggerHeartsAnimation,
      selectedAmount,
      form,
      suggestedAmountsToShow,
      rotate,
      reverseRotate,
      defaultDonationAmountVariant,
      hasChangedDonationAmount,
      logAmplitudeEvent,
      setHasChangedDonationAmount,
    ]
  );

  /**
   * Select the third suggested amount if the default amount variant is "b"
   */
  useEffect(() => {
    if (defaultDonationAmountVariant === "b") {
      handleOnChangeAmountFromSuggestion({
        value: suggestedAmountsToShow[2].amount.toString(),
        triggeredByUseEffect: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDonationAmountVariant, suggestedAmountsToShow.length]);

  const handleOnManualAmountInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const textValue = e.target.value;
    const floatValue = parseFloat(textValue);
    const value = isNaN(floatValue) ? 0 : Math.round(floatValue * 100);

    if (!value || value < selectedAmount) {
      reverseRotate();
    } else if (value > selectedAmount) {
      triggerHeartsAnimation();
      rotate();
    }

    form.setValue("amount", value ?? 0, { shouldValidate: true });
    form.setValue("manualAmount", textValue, { shouldValidate: true });
    setDisplayedFormAmount(value ?? 0);
    // onManualInputChange(textValue);

    const matchingSuggestedAmount = suggestedAmountsToShow.find((amount) => amount.amount === value);
    form.setValue("selectedAmountId", matchingSuggestedAmount?.id);

    if (defaultDonationAmountVariant === "b" && !hasChangedDonationAmount) {
      setHasChangedDonationAmount(true);
      logAmplitudeEvent(AmplitudeEvents.DefaultAmountChanged);
    }
  };

  // Prefill and trigger validation on amount field if query param is present
  useEffect(() => {
    if (defaultAmountQueryParam) {
      setTimeout(() => {
        // add timeout to be sure the continue button is displayed when page loads
        handleOnManualAmountInputChanged({
          target: { value: defaultAmountQueryParam.toString() },
        } as React.ChangeEvent<HTMLInputElement>);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAmountQueryParam]);

  const hasSuggestedAmounts =
    hasSuggestedAmountsPerType.oneTime || hasSuggestedAmountsPerType.monthly || hasSuggestedAmountsPerType.yearly;
  const getAmountWording = () => {
    if (amountRecurrenceInterval.onlyMonthly) {
      return t("donationForm", "form.chooseMonthlyAmount");
    }

    if (amountRecurrenceInterval.onlyYearly) {
      return t("donationForm", "form.chooseYearlyAmount");
    }

    if (hasSuggestedAmounts) {
      return t("donationForm", "form.chooseAmount");
    }

    return t("donationForm", "form.donationAmount");
  };

  return (
    <Stack gap={{ xs: 1, md: 1.5 }} sx={{ opacity: isGhostPreview ? GHOST_PREVIEW_OPACITY : 1 }} id="amounts">
      <FormTypography variant="subtitle1" vibe="intense">
        {getAmountWording()}:
      </FormTypography>
      {/* Recurrence Buttons */}
      {!hideSuggestedAmounts && !amountRecurrenceInterval.onlyOneType && (
        <FormTabGroup
          value={recurrenceInterval}
          onChange={(_, newRecurrence) => handleRecurringClick(newRecurrence as StripeRecurringInterval | null)}
        >
          {hasSuggestedAmountsPerType.oneTime && (
            <FormTab value={null} label={capitalize(t("donationForm", "oneTime"))} data-test="amount-one-time" />
          )}
          {hasSuggestedAmountsPerType.monthly && (
            <HeartsFormTab
              value={StripeRecurringInterval.month}
              label={capitalize(t("donationForm", "monthly"))}
              data-test="amount-monthly"
            />
          )}
          {hasSuggestedAmountsPerType.yearly && (
            <FormTab
              value={StripeRecurringInterval.year}
              label={capitalize(t("donationForm", "yearly"))}
              data-test="amount-yearly"
            />
          )}
        </FormTabGroup>
      )}
      {/* Suggested Amounts */}
      {!hideSuggestedAmounts && (
        <FormRadioGroup
          value={selectedAmount}
          onChange={(_, value) => handleOnChangeAmountFromSuggestion({ value, triggeredByUseEffect: false })}
        >
          {suggestedAmountsToShow.map(({ amount, id }) => (
            <FormRadio
              key={id}
              value={amount ?? 0} // In preview mode, the amount can be undefined and lead to weird color behavior, we set it to 0 to avoid it
              label={
                amount
                  ? formatSmartNarrowCurrencyFromCents(amount, {
                      locale: isoLocale,
                      currency,
                    })
                  : "$..."
              }
              data-test="suggested-amount-button"
              checked={selectedAmountId === id}
            />
          ))}
        </FormRadioGroup>
      )}
      {/* Manual Amount Input */}
      <Stack gap={0.5}>
        <FormTextField
          name="manualAmount"
          control={form.control}
          type="number"
          size="large"
          placeholder={hasSuggestedAmounts ? t("donationForm", "other") : "0.00"}
          startAdornment={donationForm?.organization?.country === OrganizationCountry.GreatBritain ? "£" : "$"}
          disableInjectingGlobalStyles={isPreview}
          endAdornment={
            recurrenceInterval === StripeRecurringInterval.month
              ? `/${t("donationForm", "month")}`
              : recurrenceInterval === StripeRecurringInterval.year
                ? `/${t("donationForm", "year")}`
                : undefined
          }
          error={!!form.formState.errors.amount?.message}
          helperText={form.formState.errors.amount?.message}
          data-test="donation-form-amount-input"
          onChange={handleOnManualAmountInputChanged}
          components={{
            endAdornment: {
              sx: (theme) => ({
                color: theme.palette.forms.text.supershy,
              }),
            },
          }}
        />
        {selectedSuggestedAmountObject?.message && (
          <FormTypography
            key={selectedSuggestedAmountObject.message}
            variant="subtitle2"
            vibe="brand"
            data-test="amount-subtitle"
            sx={{
              animation: "suggested-amount-message-fade-in ease-out 200ms",
              ["@keyframes suggested-amount-message-fade-in"]: {
                from: {
                  translate: "16px 0",
                  opacity: 0,
                },
                to: {
                  translate: "0 0",
                  opacity: 1,
                },
              },
            }}
          >
            {selectedSuggestedAmountObject.message}
          </FormTypography>
        )}
      </Stack>
    </Stack>
  );
};
